import { AuthProvider, HttpError } from "react-admin";
import { mutateSignIn, signInErrorCodes } from "api/mutations/signIn";
import { mutateSignOut } from "api/mutations/signOut";
import StandardApiError from "shared/errors/standardApiError";
import { getHandledErrorsFromRequestResult } from "shared/errors/errorHandling";
import { logout, profile, signIn } from "store/auth/actions";
import { getProfile, hasProfile, isAuthenticated } from "store/auth/query";
import { fetchUserProfile } from "api/fetchers/userProfile";
import { store } from "store";
import { Capability } from "reactAdmin/capabilities";

export type isAuthorized = ((capability: string) => boolean);

const authProvider: AuthProvider = {
  // authentication
  login: async ({ username, password }) => {
    const values = {
      emailAddress: username,
      password,
    };
    try {
      const result = await mutateSignIn(values);
      signIn(result.data.token);
    } catch (e) {
      const handledErrors = getHandledErrorsFromRequestResult(
        e,
        signInErrorCodes
      );
      return Promise.reject(
          e instanceof StandardApiError && handledErrors.getAllMessages().length
          ? new HttpError(
              handledErrors.getAllMessages().join(", "),
              e.response.status
            )
          : undefined
      );
    }
  },
  checkError: (error) => {
    if (error instanceof StandardApiError) {
      if (error.response.status === 401 || error.response.status === 403) {
        logout();
        return Promise.reject();
      }
    }
    return Promise.resolve();
  },
  checkAuth: async (params) => {
    return isAuthenticated() ? Promise.resolve() : Promise.reject();
  },
  logout: async () => {
    if (isAuthenticated()) {
      await mutateSignOut();
    }
    logout();
  },
  getIdentity: async () => {
    if (isAuthenticated() && !hasProfile()) {
      store.dispatch(profile(await fetchUserProfile()));
    }
    const prof = getProfile();

    if (prof === undefined) {
      return Promise.reject();
    }

    return {
      ...prof,
      fullName: prof.displayName,
      avatar: prof.avatar !== null ? prof.avatar.src : undefined,
    };
  },

  // authorization
  getPermissions: async (params): Promise<isAuthorized> => {
    if (isAuthenticated() && !hasProfile()) {
      try {
        store.dispatch(profile(await fetchUserProfile()));
      } catch (error) {
        // Can not fetch profile
      }
    }

    const prof = getProfile();
    const capabilities = prof !== undefined ? prof.capabilities : [];
    capabilities.push({
      capability: Capability.ACCESS_RESOURCES,
      authorized: prof !== undefined,
    });

    return (capability) =>
        capabilities.some(
            (item) => item.capability === capability && item.authorized
        );
  },

};

export default authProvider;
